import { Link } from "react-router-dom";
import {
  Brain, Calendar, Facebook, FileText, HelpCircle, Instagram, LayoutGrid, Linkedin,
  Mail, MapPin, Megaphone, MessageSquare, Phone, ShieldCheck, TrendingUp, Users,
  Wallet, Zap,
} from "lucide-react";
import { Button } from "@/shared/ui/button.jsx";
import { Card, CardContent } from "@/shared/ui/card.jsx";
import { InstitutionalHeader } from "./components/InstitutionalHeader.jsx";
import { Hero } from "./components/Hero.jsx";
import logo from "@/assets/odontoplug-logo.png";
import plugMark from "@/assets/plug-mark.png";

/**
 * Página institucional.
 *
 * Mesmo conteúdo e mesmos botões do site atual. O que mudou: o conteúdo
 * (benefícios, módulos, planos, dúvidas) virou dado no topo do arquivo, em
 * vez de dez blocos de JSX quase iguais escritos à mão.
 */

const BENEFICIOS = [
  { title: "Centralize toda a operação", desc: "Diga adeus à fragmentação. Tenha tudo sob controle em uma única tela.", icon: LayoutGrid },
  { title: "Automatize pelo WhatsApp", desc: "Confirmações, lembretes e pós-consulta automáticos que economizam tempo.", icon: MessageSquare },
  { title: "Organize seus pacientes", desc: "CRM focado na jornada do paciente, do primeiro contato à fidelização.", icon: Users },
  { title: "Controle financeiro real", desc: "Fluxo de caixa, orçamentos e indicadores estratégicos em tempo real.", icon: Wallet },
  { title: "Aumente a produtividade", desc: "Fluxos de trabalho otimizados para que sua equipe foque no que importa.", icon: Zap },
  { title: "Decisões baseadas em dados", desc: "Relatórios de BI que mostram exatamente onde sua clínica pode crescer.", icon: TrendingUp },
];

const MODULOS = [
  { title: "CRM de Pacientes", desc: "Histórico completo, funil de atendimento e acompanhamento de tratamentos.", icon: Users },
  { title: "Agenda Inteligente", desc: "Agendamentos, confirmações automáticas e redução drástica de faltas.", icon: Calendar },
  { title: "Prontuário Eletrônico", desc: "Registro completo, evolução clínica e anexos em um ambiente seguro.", icon: FileText },
  { title: "Financeiro", desc: "Gestão de receitas, despesas, fluxo de caixa e dashboards financeiros.", icon: Wallet },
  { title: "WhatsApp Business", desc: "Atendimento centralizado com múltiplos números e histórico unificado.", icon: MessageSquare },
  { title: "Marketing", desc: "Captação de leads, gestão de campanhas e conversão otimizada.", icon: Megaphone },
  { title: "Automações", desc: "Jornada do paciente automatizada: lembretes, pós-consulta e reativação.", icon: Zap },
  { title: "Business Intelligence", desc: "Dashboards estratégicos com os principais KPIs da sua clínica.", icon: TrendingUp },
  { title: "Inteligência Artificial", desc: "Sugestões inteligentes e análises preditivas para otimizar sua gestão.", icon: Brain },
];

const DIFERENCIAIS = [
  "Plataforma 100% em nuvem",
  "Integração Nativa com WhatsApp",
  "CRM Especializado Odonto",
  "Inteligência Artificial Integrada",
  "Automações Avançadas",
  "Segurança de Dados de Ponta",
  "Escalabilidade Multiunidades",
  "Interface Intuitiva e Rápida",
];

const PLANOS = [
  {
    name: "Starter",
    desc: "Para clínicas que estão começando a digitalizar a operação.",
    features: ["Até 3 usuários", "Agenda básica", "Prontuário simplificado", "Financeiro básico"],
  },
  {
    name: "Professional",
    desc: "O plano mais popular para clínicas em crescimento acelerado.",
    features: ["Usuários ilimitados", "Integração WhatsApp", "CRM Completo", "Automações básicas", "BI Básico"],
    highlight: true,
  },
  {
    name: "Enterprise",
    desc: "Solução completa para grandes clínicas e multiunidades.",
    features: ["Tudo do Profissional", "Multiunidades", "IA Avançada", "Suporte VIP", "BI Customizado"],
  },
];

const DUVIDAS = [
  { q: "Preciso instalar algo?", a: "Não. A OdontoPlug é 100% em nuvem. Você acessa de qualquer navegador no computador ou celular." },
  { q: "Funciona em celular?", a: "Sim, nossa plataforma é totalmente responsiva e otimizada para dispositivos móveis." },
  { q: "Tem integração com WhatsApp?", a: "Sim, temos uma integração nativa avançada para automações e atendimento centralizado." },
  { q: "É seguro?", a: "As senhas são armazenadas com hash, as credenciais de integração ficam cifradas e cada clínica só enxerga os próprios dados." },
  { q: "Posso usar em mais de uma unidade?", a: "Sim, nosso plano Enterprise é focado em redes de clínicas e multiunidades." },
  { q: "Existe suporte?", a: "Com certeza. Temos uma equipe de especialistas pronta para te ajudar via chat, e-mail ou WhatsApp." },
];

const CONTATO = {
  telefone: "+55 47 9 8883-2333",
  email: "contato@odontoplug.com.br",
  endereco: ["Av. Vereador Abrahão João Francisco, 2957", "Ressacada - Itajaí/SC - CEP 88.307-303"],
};

export function LandingPage() {
  return (
    <div className="min-h-screen bg-background selection:bg-primary/20 selection:text-primary">
      <InstitutionalHeader />

      <main>
        <Hero />

        {/* --------------------------------------------------- benefícios */}
        <section id="recursos" className="bg-secondary/30 py-24">
          <div className="container mx-auto px-4">
            <TituloSecao
              title="A operação da sua clínica, simplificada."
              subtitle="Tudo o que você precisa para gerir e crescer em um ecossistema digital completo e intuitivo."
            />
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {BENEFICIOS.map((item) => (
                <Card
                  key={item.title}
                  className="border-border/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                >
                  <CardContent className="p-8">
                    <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                      <item.icon className="h-6 w-6" />
                    </div>
                    <h3 className="mb-3 font-display text-xl font-bold">{item.title}</h3>
                    <p className="leading-relaxed text-muted-foreground">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------ módulos */}
        <section id="solucoes" className="py-24">
          <div className="container mx-auto px-4">
            <TituloSecao
              title="Tudo em um só lugar"
              subtitle="Módulos integrados que conversam entre si para criar uma experiência fluida para você e seus pacientes."
            />
            <div className="grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
              {MODULOS.map((modulo) => (
                <div key={modulo.title} className="flex gap-6">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-lg shadow-primary/20">
                    <modulo.icon className="h-7 w-7" />
                  </div>
                  <div>
                    <h3 className="mb-2 font-display text-lg font-bold">{modulo.title}</h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">{modulo.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* -------------------------------------------------- diferenciais */}
        <section className="relative overflow-hidden bg-primary py-24 text-primary-foreground">
          <div className="absolute right-0 top-0 p-24 opacity-10">
            <img src={plugMark} alt="" className="h-96 w-96 rotate-12" />
          </div>
          <div className="container relative z-10 mx-auto px-4">
            <div className="max-w-3xl">
              <h2 className="mb-6 font-display text-4xl font-bold md:text-5xl">
                Mais do que gestão. Crescimento.
              </h2>
              <p className="mb-12 text-lg text-primary-foreground/80">
                Desenvolvemos a OdontoPlug para ser o motor de tração da sua clínica, unindo
                tecnologia de ponta e uma interface que sua equipe vai amar usar.
              </p>
              <div className="grid gap-x-12 gap-y-8 sm:grid-cols-2">
                {DIFERENCIAIS.map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/20">
                      <ShieldCheck className="h-4 w-4" />
                    </div>
                    <span className="font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------- planos */}
        <section id="planos" className="bg-secondary/30 py-24">
          <div className="container mx-auto px-4">
            <TituloSecao
              title="Planos que crescem com você"
              subtitle="Escolha o pacote ideal para o momento da sua clínica."
            />
            <div className="grid gap-8 md:grid-cols-3">
              {PLANOS.map((plano) => (
                <Card
                  key={plano.name}
                  className={`relative overflow-hidden border-border/50 transition-transform duration-300 hover:-translate-y-2 ${
                    plano.highlight ? "z-10 scale-105 shadow-2xl shadow-primary/10 ring-2 ring-primary" : ""
                  }`}
                >
                  {plano.highlight && (
                    <div className="absolute right-0 top-0 rounded-bl-lg bg-primary px-4 py-1.5 text-[10px] font-bold uppercase tracking-widest text-primary-foreground">
                      Mais Popular
                    </div>
                  )}
                  <CardContent className="p-8">
                    <h3 className="mb-2 font-display text-2xl font-bold">{plano.name}</h3>
                    <p className="mb-8 min-h-10 text-sm text-muted-foreground">{plano.desc}</p>

                    <div className="mb-8">
                      <span className="text-3xl font-bold">Consulte</span>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Preços sob medida para sua escala
                      </p>
                    </div>

                    <div className="mb-10 space-y-4">
                      {plano.features.map((f) => (
                        <div key={f} className="flex items-center gap-3 text-sm">
                          <Zap className="h-3.5 w-3.5 shrink-0 text-primary" />
                          <span>{f}</span>
                        </div>
                      ))}
                    </div>

                    <Button
                      asChild
                      className="w-full rounded-full"
                      variant={plano.highlight ? "default" : "outline"}
                    >
                      <a href={`mailto:${CONTATO.email}?subject=Quero o plano ${plano.name}`}>
                        Começar Agora
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------ dúvidas */}
        <section className="py-24">
          <div className="container mx-auto max-w-4xl px-4">
            <TituloSecao
              title="Perguntas Frequentes"
              subtitle="Tire suas dúvidas sobre a OdontoPlug."
            />
            <div className="space-y-4">
              {DUVIDAS.map((duvida) => (
                <div key={duvida.q} className="rounded-xl border border-border bg-card p-6">
                  <h4 className="mb-3 flex items-center gap-3 font-display font-bold">
                    <HelpCircle className="h-5 w-5 shrink-0 text-primary" />
                    {duvida.q}
                  </h4>
                  <p className="text-sm leading-relaxed text-muted-foreground">{duvida.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ----------------------------------------------------- chamada */}
        <section className="bg-primary/5 py-24">
          <div className="container mx-auto px-4 text-center">
            <div className="mx-auto max-w-3xl rounded-3xl border border-border bg-card p-12 shadow-2xl shadow-primary/5">
              <h2 className="mb-6 font-display text-3xl font-bold md:text-4xl">
                Pronto para levar sua clínica para o próximo nível?
              </h2>
              <p className="mb-10 text-lg text-muted-foreground">
                Junte-se a centenas de clínicas que já transformaram sua gestão com a OdontoPlug.
              </p>
              <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
                <Button asChild size="lg" className="h-14 rounded-full px-10 text-base">
                  <a href={`mailto:${CONTATO.email}?subject=Quero uma demonstração`}>
                    Solicitar Demonstração
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline" className="h-14 rounded-full px-10 text-base">
                  <a href={`https://wa.me/${CONTATO.telefone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer">
                    Falar com Especialista
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* ------------------------------------------------------- rodapé */}
      <footer id="contato" className="border-t border-border bg-background py-20">
        <div className="container mx-auto px-4">
          <div className="mb-16 grid gap-12 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-6">
              <img src={logo} alt="OdontoPlug" className="h-8 w-auto dark:brightness-0 dark:invert" />
              <p className="text-sm leading-relaxed text-muted-foreground">
                A plataforma que conecta inteligência, gestão e crescimento para clínicas
                odontológicas modernas.
              </p>
              <div className="flex gap-3">
                {[Instagram, Linkedin, Facebook].map((Icon, i) => (
                  <a
                    key={i}
                    href="#"
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:border-primary hover:text-primary"
                    aria-label="Rede social"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>

            <ColunaRodape titulo="Recursos">
              <a href="#solucoes" className="transition-colors hover:text-primary">CRM Comercial</a>
              <a href="#solucoes" className="transition-colors hover:text-primary">Agenda Inteligente</a>
              <a href="#solucoes" className="transition-colors hover:text-primary">WhatsApp Business</a>
              <a href="#solucoes" className="transition-colors hover:text-primary">Financeiro</a>
            </ColunaRodape>

            <ColunaRodape titulo="Empresa">
              <a href="#recursos" className="transition-colors hover:text-primary">Sobre Nós</a>
              <a href="#planos" className="transition-colors hover:text-primary">Planos</a>
              <Link to="/privacidade" className="transition-colors hover:text-primary">
                Política de Privacidade
              </Link>
              <Link to="/termos" className="transition-colors hover:text-primary">
                Termos de Uso
              </Link>
            </ColunaRodape>

            <div className="space-y-6">
              <h4 className="font-display font-bold">Contato</h4>
              <div className="space-y-4 text-sm text-muted-foreground">
                <p className="flex gap-3">
                  <Phone className="h-4 w-4 shrink-0 text-primary" />
                  {CONTATO.telefone}
                </p>
                <p className="flex gap-3">
                  <Mail className="h-4 w-4 shrink-0 text-primary" />
                  {CONTATO.email}
                </p>
                <p className="flex gap-3">
                  <MapPin className="h-4 w-4 shrink-0 text-primary" />
                  <span>
                    {CONTATO.endereco[0]}
                    <br />
                    {CONTATO.endereco[1]}
                  </span>
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-center justify-between gap-4 border-t border-border pt-8 text-xs text-muted-foreground md:flex-row">
            <p>© {new Date().getFullYear()} OdontoPlug. Todos os direitos reservados.</p>
            <div className="flex items-center gap-2">
              <span>Feito com</span>
              <Zap className="h-3 w-3 fill-primary text-primary" />
              <span>para grandes dentistas</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function TituloSecao({ title, subtitle }) {
  return (
    <div className="mb-16 text-center">
      <h2 className="mb-4 font-display text-3xl font-bold md:text-4xl">{title}</h2>
      <p className="mx-auto max-w-2xl text-lg text-muted-foreground">{subtitle}</p>
    </div>
  );
}

function ColunaRodape({ titulo, children }) {
  return (
    <div>
      <h4 className="mb-6 font-display font-bold">{titulo}</h4>
      <div className="flex flex-col gap-4 text-sm text-muted-foreground">{children}</div>
    </div>
  );
}
